import type { AuthHeaders } from '../client'
import { request } from '../client'
import type {
  JobDetail,
  JobListResponse,
  JobSubmitRequest,
  JobSubmitResponse,
  JobType,
} from '../types'
import { JOB_SESSION_REQUIREMENTS } from '../types'

/**
 * Resolve which headers a job of `type` actually needs. Mirrors sprint-api's
 * preflight: if a job doesn't require Score, we omit the header entirely so
 * expired Score sessions don't needlessly trigger 401 Score reauth flows.
 */
export function buildJobAuthHeaders(
  auth: AuthHeaders,
  type: JobType,
): AuthHeaders {
  const required = JOB_SESSION_REQUIREMENTS[type]
  return {
    accessToken: auth.accessToken,
    providerSession: required.emjogo ? auth.providerSession : undefined,
    scoreSession: required.score ? auth.scoreSession ?? undefined : undefined,
  }
}

export function submitJob(
  auth: AuthHeaders,
  body: JobSubmitRequest,
): Promise<JobSubmitResponse> {
  return request<JobSubmitResponse>('/jobs', {
    method: 'POST',
    body,
    auth: buildJobAuthHeaders(auth, body.type),
  })
}

export function listJobs(
  auth: AuthHeaders,
  params: { limit?: number; skip?: number } = {},
): Promise<JobListResponse> {
  return request<JobListResponse>('/jobs', {
    query: { limit: params.limit, skip: params.skip },
    auth,
  })
}

export function getJob(
  auth: AuthHeaders,
  jobId: string,
): Promise<JobDetail> {
  return request<JobDetail>(`/jobs/${encodeURIComponent(jobId)}`, { auth })
}

export function cancelJob(
  auth: AuthHeaders,
  jobId: string,
): Promise<JobDetail> {
  return request<JobDetail>(`/jobs/${encodeURIComponent(jobId)}`, {
    method: 'DELETE',
    auth,
  })
}
